import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Row, Col, Form, Tooltip } from 'antd'
import Context, { ContextValueType } from '../../ConfigProvider/context'
import connectProvider from 'gyl-common/hocs/connectProvider'
import styles from './index.less'

import FieldsInHooksType from './typing'
import { LayoutWrap } from '../../hooks/useQueryLayout'
import SearchArea from '../SearchArea'

const ItemComponent = ({ item, form, initialValues, ...restProps }) => {
    return (
        <Tooltip
            overlay={null}
            title={item.tips}
            trigger="focus"
            overlayStyle={{ maxWidth: '225px', fontSize: '12px' }}
            color="rgba(0,0,0,0.7)"
        >
            <item.component
                form={form}
                name={item.name}
                {...restProps}
                {...item.props}
                {...(typeof item.renderProps === 'function'
                    ? item.renderProps({
                          form,
                          initialValues
                      })
                    : {})}
                className={classNames(styles.item)}
            />
        </Tooltip>
    )
}

const FieldItemList: React.FC<FieldsInHooksType & ContextValueType> = props => {
    const {
        fieldsList,
        form,
        colNumPerLine,
        initialValues,
        showBtn,
        showLabel,
        colStyle,
        collapsed,
        contentAfterLastFieldItem,
        hideSearchArea
    } = props

    const isOverOneLine = useMemo(() => {
        try {
            if (fieldsList[fieldsList.length - 1]) {
                return fieldsList[fieldsList.length - 1]._rowIndex > 0
            }
        } catch (error) {
            console.error('catchError', error)
        }

        return false
    }, [fieldsList])

    if (contentAfterLastFieldItem && collapsed) {
        const hiddenIndex = fieldsList.findIndex(_ => _._hidden)
        if (hiddenIndex > -1 && fieldsList[hiddenIndex - 1]) {
            fieldsList[hiddenIndex - 1]._hidden = true
        }
    }

    // 只有一行时，按钮放在右边
    return (
        <div className={classNames(styles.fieldItemList, styles.normalVerticalSize)}>
            <div className={classNames(styles.filter)}>
                <Row gutter={4}>
                    {fieldsList.map(item => {
                        let shouldUpdate = item.options?.shouldUpdate
                        if (shouldUpdate === true) {
                            shouldUpdate = () => true
                        }
                        if (!shouldUpdate) {
                            shouldUpdate = () => false
                        }

                        if (item._hidden) {
                            return (
                                <Form.Item key={item?.name} {...item.options} hidden name={item.name} noStyle>
                                    <item.component
                                        form={form}
                                        name={item.name}
                                        {...item.props}
                                        {...(typeof item.renderProps === 'function'
                                            ? item.renderProps({
                                                  form,
                                                  initialValues
                                              })
                                            : {})}
                                        className={classNames(styles.item)}
                                    />
                                </Form.Item>
                            )
                        }

                        return (
                            <LayoutWrap
                                key={item?.key || item.name}
                                field={item}
                                colNumPerLine={colNumPerLine}
                                colStyle={colStyle}
                                style={item.warpStyle || item.style}
                            >
                                <Form.Item shouldUpdate={shouldUpdate} noStyle>
                                    {() => {
                                        return (
                                            <Col style={item.style}>
                                                <Form.Item
                                                    {...item.options}
                                                    name={item.name}
                                                    label={showLabel ? item.label : undefined}
                                                >
                                                    {item.tips ? (
                                                        <ItemComponent
                                                            item={item}
                                                            form={form}
                                                            initialValues={initialValues}
                                                        />
                                                    ) : (
                                                        <item.component
                                                            form={form}
                                                            name={item.name}
                                                            {...item.props}
                                                            {...(typeof item.renderProps === 'function'
                                                                ? item.renderProps({
                                                                      form,
                                                                      initialValues
                                                                  })
                                                                : {})}
                                                            className={classNames(styles.item)}
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        )
                                    }}
                                </Form.Item>
                            </LayoutWrap>
                        )
                    })}
                    {!!contentAfterLastFieldItem && (
                        <LayoutWrap field={{}} colNumPerLine={colNumPerLine} colStyle={colStyle}>
                            {typeof contentAfterLastFieldItem === 'function'
                                ? contentAfterLastFieldItem()
                                : contentAfterLastFieldItem}
                        </LayoutWrap>
                    )}
                    {!hideSearchArea && isOverOneLine && showBtn && (
                        <Col className={styles.btnWrap}>
                            <SearchArea />
                        </Col>
                    )}
                </Row>
            </div>

            {!hideSearchArea && !isOverOneLine && showBtn && (
                <Col className={styles.btnWrap2}>
                    <SearchArea />
                </Col>
            )}
        </div>
    )
}

const mapStateToProps = (context: ContextValueType) => {
    return {
        form: context?.form,
        initialValues: context?.initialValues,
        colNumPerLine: context?.colNumPerLine,
        contentAfterLastFieldItem: context?.contentAfterLastFieldItem,
        collapsed: context?.collapsed,
        hideSearchArea: context.quickSearchEnable || context?.hideSearchArea
    }
}

export default connectProvider(mapStateToProps)(FieldItemList, Context)
